import { getSessionStorage } from '@trinity/chat-components/lib/storage';

const SIDE_PANEL_OPEN_STATE_KEY_PREFIX = 'trinity:chat:side-panel-open-state:v1';
// Legacy unscoped key, kept only so any value written before scope-awareness was
// introduced doesn't leak into every scope forever (see readSidePanelOpenState).
const LEGACY_SIDE_PANEL_OPEN_STATE_KEY = SIDE_PANEL_OPEN_STATE_KEY_PREFIX;

export type SidePanelOpenState = 'open' | 'closed';

// Utility functions for managing the open/closed state of the side panel in session storage
// to override the default state once the application is loaded.
//
// State is scoped by an optional `scopeKey` (e.g. project/entity/context-type identity) so that
// an explicit close in one scope (a project, document, folder, transcript, etc.) doesn't
// suppress auto-open in a different scope. Callers that don't pass a `scopeKey` share a single
// "default" bucket, preserving prior behavior for those call sites.
function isSidePanelOpenState(value: string | null): value is SidePanelOpenState {
  return value === 'open' || value === 'closed';
}

function getSidePanelOpenStateKey(scopeKey?: string | null): string {
  return `${SIDE_PANEL_OPEN_STATE_KEY_PREFIX}:${scopeKey ?? 'default'}`;
}

export function readSidePanelOpenState(scopeKey?: string | null): SidePanelOpenState | null {
  try {
    const storage = getSessionStorage();
    const value = storage.getItem(getSidePanelOpenStateKey(scopeKey));
    if (isSidePanelOpenState(value)) {
      return value;
    }
    // Legacy unscoped value only ever applies to the default bucket (no scopeKey) —
    // never fall back to it for a real scope, and migrate it into the default bucket
    // key once so it isn't re-read afterwards.
    if (scopeKey) {
      return null;
    }
    const legacyValue = storage.getItem(LEGACY_SIDE_PANEL_OPEN_STATE_KEY);
    if (isSidePanelOpenState(legacyValue)) {
      storage.setItem(getSidePanelOpenStateKey(scopeKey), legacyValue);
      storage.removeItem(LEGACY_SIDE_PANEL_OPEN_STATE_KEY);
      return legacyValue;
    }
    return null;
  } catch {
    return null;
  }
}

export function writeSidePanelOpenState(value: SidePanelOpenState, scopeKey?: string | null): void {
  try {
    getSessionStorage().setItem(getSidePanelOpenStateKey(scopeKey), value);
  } catch {
    // Swallow storage failures; the panel should still function without persistence.
  }
}

// Whether any scope whose key contains `fragment` was explicitly closed. Lets a caller that can't
// yet build the exact scope key (e.g. the destination of a navigation, before its context loads)
// still honour a saved "closed" preference.
export function hasClosedSidePanelOpenStateMatching(fragment: string): boolean {
  try {
    const storage = getSessionStorage();
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (
        key?.startsWith(`${SIDE_PANEL_OPEN_STATE_KEY_PREFIX}:`) &&
        key.includes(fragment) &&
        storage.getItem(key) === 'closed'
      ) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}

export function clearSidePanelOpenState(scopeKey?: string | null): void {
  try {
    getSessionStorage().removeItem(getSidePanelOpenStateKey(scopeKey));
  } catch {
    // Swallow storage failures; the panel should still function without persistence.
  }
}